import React, { useMemo } from 'react';
import ListingWrapper from './ListingWrapper';
import TopBar from './TopBar';
import { menuItems } from '../utils/helper';
import { cn } from '@ui/atoms/utils/utils';
import { useSearchParams } from 'next/navigation';
import { OPPORTUNITY_BOND_TYPES, OPPORTUNITY_TABS } from '../utils/constants';
import { useRouter } from 'next/router';
import { Image, Text } from '@ui/atoms';
import { useStoreSelector } from '@ui/hooks/useStoreSelector';
import Button from '@ui/atoms/design-system/Button';
import useOpportunitiesStore from '@components/OpportunityListing/store/useOpportunitiesStore';
import {
    getAppliedFilters,
    getCachedData,
} from '@components/OpportunityListing/utils/utils';

import {
    activeOpportunityListingGaEventsName,
    opportunitiesGaEventsName,
} from '@helpers/ga-events-name-enum';
import { triggerCustomGaEvent } from '@helpers/miscelleanous';
import DownloadAppBanner from './DownloadAppBanner';
import { showAppDownloadBanner } from '@helpers/constants';
import useWindowDimensions from '@hooks/useWindowDimensions';
import ViewOtherOpportunitiesButton from '@components/OpportunityListing/components/ViewOtherOpportunitiesButton';
import { useAppNudgeVisibility } from '@hooks/useAppNudgeVisibility';
import { getAuth } from '@services/identity.service';
import APP_NUDGE_ENUMS from '@helpers/enums/app-nudge-enums';

const OpportunityHeader = ({
    showActiveOpportunities = true,
    renderBanner,
}) => {
    const auth = getAuth();
    const searchParams = useSearchParams();
    const queryParams = Object.fromEntries(searchParams.entries());
    const { count: filterCount } = getAppliedFilters(queryParams);
    const router = useRouter();
    const activeTab = searchParams.get('type') || OPPORTUNITY_BOND_TYPES.BOND;
    const { width } = useWindowDimensions();
    const { stats, isAuthorized } = useStoreSelector(
        useOpportunitiesStore,
        (state) => ({
            setStatus: state.setStatus,
            stats: state.stats,
            isAuthorized: state.isAuthorized,
        })
    );

    const { showAppNudge, isLoading } = useAppNudgeVisibility({
        investorId: auth?.id,
        path: APP_NUDGE_ENUMS.OPPORTUNITY_LISTING,
    });

    const tabCounts = useMemo(() => {
        return {
            bonds: stats?.bonds || 0,
            sdis: (stats?.sdi || 0),
            tBills: stats?.tBills || 0,
        };
    }, [stats]);

    const triggerGaEventsForOpportunitiesTabs = (value) => {
        let eventName;
    
        const activeEvents = {
            [OPPORTUNITY_TABS.BONDS]: opportunitiesGaEventsName.CLICKED_VIEW_BONDS_TAB_ACTIVE,
            [OPPORTUNITY_TABS.SDIS]: opportunitiesGaEventsName.CLICKED_VIEW_SDI_TAB_ACTIVE,
            [OPPORTUNITY_TABS.TBILLS]: opportunitiesGaEventsName.CLICKED_VIEW_TBILLS_TAB_ACTIVE,
        };
        
        const closedEvents = {
            [OPPORTUNITY_TABS.BONDS]: opportunitiesGaEventsName.CLICKED_VIEW_BONDS_TAB_RECENTLY_CLOSED,
            [OPPORTUNITY_TABS.SDIS]: opportunitiesGaEventsName.CLICKED_VIEW_SDI_TAB_RECENTLY_CLOSED,
            [OPPORTUNITY_TABS.TBILLS]: opportunitiesGaEventsName.CLICKED_VIEW_TBILLS_TAB_RECENTLY_CLOSED,
        };
        
        const unauthorizedEvents = {
            [OPPORTUNITY_TABS.BONDS]: activeOpportunityListingGaEventsName.CLICKED_ACTIVE_OPP_VIEW_BONDS_TAB,
            [OPPORTUNITY_TABS.SDIS]: activeOpportunityListingGaEventsName.CLICKED_ACTIVE_OPP_VIEW_SDI_TAB,
            [OPPORTUNITY_TABS.TBILLS]: activeOpportunityListingGaEventsName.CLICKED_ACTIVE_OPP_VIEW_TBILLS_TAB,
        };
        
        if (isAuthorized) {
            eventName = showActiveOpportunities ? activeEvents[value] : closedEvents[value];
        } else {
            eventName = unauthorizedEvents[value];
        }
        
        if (eventName) {
            triggerCustomGaEvent(eventName);
        }
    }

    const handleTabChange = (type, tab) => {
        const { cachedData } = getCachedData(
            isAuthorized,
            showActiveOpportunities
        );
        router.push(
            {
                query: {
                    type,
                },
            },
            undefined,
            { shallow: true }
        );
        triggerGaEventsForOpportunitiesTabs(
            tab?.label
        );
    };

    const getTabCount = (type) => {
        switch (type) {
            case OPPORTUNITY_BOND_TYPES.BOND:
                return tabCounts.bonds;
            case OPPORTUNITY_BOND_TYPES.SDIS:
                return tabCounts.sdis;
            case OPPORTUNITY_BOND_TYPES.TreasuryBill:
                return tabCounts.tBills;
            default:
                return 0;
        }
    };

    const openOtherOpportunitiesPage = () => {
        const params = searchParams.toString();
        const queryString = params ? `?${params}` : '';

        const newPath = showActiveOpportunities
            ? `/opportunities/closed${queryString}`
            : `/opportunities${queryString}`;

        router.push(newPath);
    };

    return (
        <ListingWrapper
            className=" bg-[url('/images/opportunities/ListingBackground.svg')] w-full bg-cover bg-center"
            tag="header"
        >
            {isAuthorized && !filterCount ? renderBanner() : null}
            <TopBar
                title={
                    showActiveOpportunities
                        ? 'Active Opportunities'
                        : 'Recently Closed Opportunities'
                }
                description={
                    showActiveOpportunities
                        ? 'Invest in curated, secured, high-yield opportunities'
                        : 'Take a glance at some of our past opportunities'
                }
                isAuthorized={isAuthorized}
                openOtherOpportunitiesPage={openOtherOpportunitiesPage}
                showActiveOpportunities={showActiveOpportunities}
                showAppNudge={showAppNudge}
            />
            {isAuthorized && (
                <ViewOtherOpportunitiesButton
                    buttonSize={width < 768 ? 'extrasmall' : 'small'}
                    buttonType="text"
                    className="!text-basicWhite hover:!text-basicWhite/90 inline-flex lg:hidden mb-6 mx-auto !w-full"
                    showActiveOpportunities={showActiveOpportunities}
                    openOtherOpportunitiesPage={openOtherOpportunitiesPage}
                />
            )}
            
            <div className="z-10 flex flex-col bg-transparent ">
                <div className="relative flex flex-row justify-between">
                    <div className="relative flex w-full px-4 overflow-visible md:items-end md:px-0 md:w-fit">
                        {menuItems.map((item, index) => (
                            <div
                                key={index}
                                className={cn(
                                    'inline-flex px-2 py-3 relative text-basicWhite w-full flex-center p6-regular md:h6-regular cursor-pointer flex-1 rounded-br-lg overflow-visible md:flex-1 md:min-w-[180px] flex-col md:flex-row gap-2',
                                    {
                                        'bg-basicWhite rounded-t-lg  text-primary-500 p6-semibold md:h6-semibold rounded-b-none':
                                            activeTab === item.id,
                                    }
                                )}
                                onClick={() => handleTabChange(item.id, item)}
                            >
                                {activeTab === item.id && (
                                    <div className="absolute bottom-0 -left-2">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="8"
                                            height="8"
                                            viewBox="0 0 8 8"
                                            fill="none"
                                        >
                                            <path
                                                d="M7.99982 0C7.99982 4.41828 4.41818 8 0 8H8L7.99982 0Z"
                                                fill="white"
                                            />
                                        </svg>
                                    </div>
                                )}
                                <Image src={item.icon} alt={item.label} />
                                <Text
                                    content={`${item.label} (${getTabCount(
                                        item.id
                                    )})`}
                                    className=""
                                />
                                {activeTab === item.id && (
                                    <div className="absolute bottom-0 -right-2">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="8"
                                            height="8"
                                            viewBox="0 0 8 8"
                                            fill="none"
                                        >
                                            <path
                                                d="M0.000179526 0C0.000179526 4.41828 3.58182 8 8 8H0L0.000179526 0Z"
                                                fill="white"
                                            />
                                        </svg>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                    {isLoading ? null : isAuthorized && !showAppNudge ? (
                        <ViewOtherOpportunitiesButton
                            buttonSize="small"
                            buttonType="text"
                            className="!text-basicWhite hover:!text-basicWhite/90 hidden lg:inline-flex"
                            openOtherOpportunitiesPage={
                                openOtherOpportunitiesPage
                            }
                            showActiveOpportunities={showActiveOpportunities}
                        />
                    ) : showAppDownloadBanner ? (
                        <div className="hidden xl:block">
                            <DownloadAppBanner />
                        </div>
                    ) : null}
                </div>
            </div>
        </ListingWrapper>
    );
};

export default OpportunityHeader;
